/**
 * Окна защиты несохранённых изменений и разбора конфликтов.
 *
 * Первое — прямой перенос §6: три варианта, ровно как в демо. Второе —
 * то, чего в демо быть не могло: там работал один пользователь, а здесь
 * нужно показать, ЧТО именно разошлось, и дать выбрать.
 */

import type { Conflict } from '../api/client.js';
import { Button, Modal } from './ui.js';
import { useCloseOnBack } from './useCloseOnBack.js';

/**
 * Перехват ухода из раздела с несохранёнными правками (§6).
 *
 * Три варианта именно в таком порядке: сохранить (ожидаемое действие),
 * не сохранять (осознанный отказ), остаться (передумал). «Не сохранять»
 * подписано цветом отказа — оно необратимо теряет правки.
 */
export function UnsavedChangesModal({
  moduleLabel,
  saving,
  onSaveAndLeave,
  onDiscardAndLeave,
  onStay,
}: {
  moduleLabel: string;
  saving: boolean;
  onSaveAndLeave: () => void;
  onDiscardAndLeave: () => void;
  onStay: () => void;
}) {
  /*
   * «Назад» здесь равносилен «Остаться».
   *
   * Выбор между «сохранить» и «не сохранять» должен быть сделан кнопкой, и
   * назначать «назад» одному из них нельзя. Но и выпускать его наружу тоже:
   * прежде он закрывал программу вместе с теми самыми несохранёнными
   * правками, о которых предупреждает это окно.
   */
  useCloseOnBack(true, onStay);

  return (
    /*
     * Клик по затемнению не закрывает: выбор между «сохранить», «не сохранять»
     * и «остаться» должен быть сделан кнопкой. Случайный промах не должен
     * решать судьбу несохранённых правок.
     */
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(28,27,24,0.5)' }}
    >
      <div className="w-full max-w-sm rounded-xl bg-bg p-5 shadow-xl">
        <p className="mb-1.5 font-display text-base font-semibold text-ink">
          Есть несохранённые изменения
        </p>
        <p className="mb-5 text-sm text-ink2">
          В разделе «{moduleLabel}» остались правки, которые вы не сохранили. Сохранить их перед
          переходом или отменить?
        </p>
        <div className="flex flex-col gap-2">
          <Button onClick={onSaveAndLeave} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить и перейти'}
          </Button>
          <button
            onClick={onDiscardAndLeave}
            disabled={saving}
            className="rounded-lg border py-2.5 text-sm disabled:opacity-40"
            style={{ borderColor: 'var(--otc-line)', color: 'var(--otc-coral)' }}
          >
            Не сохранять
          </button>
          <button onClick={onStay} className="rounded-lg py-2 text-sm text-ink2">
            Остаться на странице
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Человеческое название вида записи — служебные имена пользователю ничего не говорят. */
const KIND_LABELS: Record<string, string> = {
  order: 'Заказ',
  clinic: 'Клиника',
  legal: 'Юрлицо',
  branch: 'Филиал',
  doctor: 'Врач',
  construction: 'Позиция справочника',
  material: 'Материал',
  color: 'Цвет',
  employee: 'Сотрудник',
  settings: 'Настройки',
};

/**
 * Разбор конфликта одновременного редактирования.
 *
 * Показывает, кто и когда изменил запись. Автоматически «слить» изменения
 * нельзя: система не знает, чья правка вернее — техника, поставившего статус
 * «Готово», или администратора, сменившего цену. Поэтому выбор явный, и оба
 * варианта названы своими словами.
 */
export function ConflictModal({
  conflicts,
  onReload,
  onKeepMine,
  onClose,
}: {
  conflicts: Conflict[];
  onReload: () => void;
  /** «Оставить моё»: перенести свои правки на свежую версию и сохранить. */
  onKeepMine?: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Данные изменены другим пользователем"
      onClose={onClose}
      size="md"
      footer={
        <>
          {onKeepMine && <Button onClick={onKeepMine}>Оставить моё</Button>}
          <Button variant={onKeepMine ? 'secondary' : 'primary'} onClick={onReload}>
            Взять версию с сервера
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Решу позже
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-ink2">
        Пока вы редактировали, эти записи изменил кто-то ещё. Ваши правки не сохранены — ничего не
        потеряно. Выберите, что делать.
      </p>

      <ul className="space-y-2 pb-2">
        {conflicts.map((conflict) => (
          <li
            key={`${conflict.kind}-${conflict.id}`}
            className="rounded-lg border px-3 py-2.5"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-ink">
                {KIND_LABELS[conflict.kind] ?? conflict.kind}
                {typeof conflict.theirs?.name === 'string' ? ` «${conflict.theirs.name}»` : ''}
              </span>
              {conflict.changedAt && (
                <span className="text-[11px] text-ink2">
                  изменено {new Date(conflict.changedAt).toLocaleString('ru-RU')}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-ink2">{conflict.message}</p>
          </li>
        ))}
      </ul>

      <p className="pb-2 text-xs text-ink2">
        «Оставить моё» сохранит поверх: ваши поля запишутся, а то, что менял другой сотрудник в
        других полях, сохранится. «Взять версию с сервера» отменит ваши правки в этом разделе.
        «Решу позже» ничего не тронет — до решения правки остаются несохранёнными.
      </p>
    </Modal>
  );
}
